import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { updateHreflangTags } from '../../utils/hreflang';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formStep, setFormStep] = useState(1); // 1 or 2
  const [consentChecked, setConsentChecked] = useState(false);
  const navigate = useNavigate();

  // Form data state
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    country: '',
    interestedIn: '',
    consultedNephrologist: '',
    onDialysis: '',
    exploringInternational: '',
    additionalInfo: ''
  });

  useEffect(() => {
    // Update meta tags
    document.title = 'Contact Us | Kidney Patient Support';

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Meta tags
    updateMetaTag(
      'description',
      'Contact Kidney Patient Support for information about overseas kidney transplant programs. Free consultation available for patients without a living donor.'
    );
    updateMetaTag(
      'keywords',
      'contact kidney transplant, kidney transplant consultation, overseas kidney transplant inquiry'
    );
    updateMetaTag('last-modified', '2025-03-01');

    // Canonical URL
    updateLinkTag('canonical', 'https://kidneypatientsupport.com/contact');

    // Hreflang tags
    updateHreflangTags({
      enPath: '/contact',
      jaPath: '/contact' // Japanese contact page exists
    });

    // Open Graph
    updateMetaTag('og:url', 'https://kidneypatientsupport.com/contact', true);
    updateMetaTag('og:title', 'Contact Us | Kidney Patient Support', true);
    updateMetaTag(
      'og:description',
      'Contact us for information about overseas kidney transplant programs. Free consultation available.',
      true
    );
    updateMetaTag('og:type', 'website', true);

    // Twitter
    updateMetaTag('twitter:url', 'https://kidneypatientsupport.com/contact');
    updateMetaTag('twitter:title', 'Contact Us | Kidney Patient Support');
    updateMetaTag(
      'twitter:description',
      'Contact us for information about overseas kidney transplant programs. Free consultation available.'
    );
    updateMetaTag('twitter:card', 'summary_large_image');

    // JSON-LD
    const createJsonLdScript = (obj: object) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
      return script;
    };

    const contactPageScript = createJsonLdScript({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Us",
      "description": "Get in touch with our kidney transplant support team.",
      "url": "https://kidneypatientsupport.com/contact",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://kidneypatientsupport.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact",
            "item": "https://kidneypatientsupport.com/contact"
          }
        ]
      }
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (contactPageScript.parentNode) {
        contactPageScript.parentNode.removeChild(contactPageScript);
      }
    };
  }, []);

  const handleStep1Continue = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStep(2);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!consentChecked) {
      setFormStatus('error');
      return;
    }

    setFormStatus('submitting');

    // Prepare form data for submission
    const submitData = new URLSearchParams();
    submitData.append('firstName', formData.firstName);
    submitData.append('email', formData.email);
    submitData.append('country', formData.country);
    submitData.append('interestedIn', formData.interestedIn);
    submitData.append('consultedNephrologist', formData.consultedNephrologist);
    submitData.append('onDialysis', formData.onDialysis);
    submitData.append('exploringInternational', formData.exploringInternational);
    submitData.append('additionalInfo', formData.additionalInfo);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d513hao9ei8qjpb9for0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: submitData.toString(),
        }
      );

      if (response.ok) {
        setFormStatus('success');
        setTimeout(() => {
          navigate('/contact/thanks');
        }, 100);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/f1b13f3c5cdde047774c7297e509c16c.png"
              alt="Kidney Patient Support"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Home
            </Link>
            <Link
              to="/ethics"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Ethics
            </Link>
            <Link
              to="/complete-guide-kidney-transplant-abroad"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Guide
            </Link>
            <Link
              to="/countries"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Countries
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Pricing
            </Link>
            <Link
              to="/process"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-gray-700 hover:text-teal-600"
            >
              Process
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium transition-colors whitespace-nowrap cursor-pointer text-teal-600"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[72px] bg-white z-40 overflow-y-auto">
            <div className="flex flex-col p-6 space-y-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Home
              </Link>
              <Link
                to="/ethics"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Ethics
              </Link>
              <Link
                to="/complete-guide-kidney-transplant-abroad"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Guide
              </Link>
              <Link
                to="/countries"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Countries
              </Link>
              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Pricing
              </Link>
              <Link
                to="/process"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Process
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-teal-600 transition-colors py-3 border-b border-gray-100 cursor-pointer"
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Request Information About <span className="text-teal-600">International Kidney Transplant Support</span>
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
              Have questions or need support? We're here to help you every step of the way.
            </p>
            
            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
              <div className="flex items-center justify-center space-x-2 bg-white rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Transparent Communication</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-white rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Ethical Framework</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-white rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">Confidential Handling</span>
              </div>
              <div className="flex items-center justify-center space-x-2 bg-white rounded-xl p-4 border border-teal-100">
                <i className="ri-checkbox-circle-fill text-teal-600 text-xl flex-shrink-0"></i>
                <span className="text-sm font-semibold text-gray-900">No Outcome Guarantees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Visit Us</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Japan Ambis International<br />
                      Tokyo, Japan
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
                    <p className="text-gray-600 leading-relaxed">
                      +81-050-6866-3030<br />
                      Mobile: +81-90-7803-0727<br />
                      Mon-Fri, 9AM-6PM JST
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                    <p className="text-gray-600 leading-relaxed">info@anbis-int.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-2xl text-teal-600"></i>
                  </div>
                  <div className="ml-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp QR Code</h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      Scan the QR code to connect with us on WhatsApp
                    </p>
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 inline-block">
                      <img
                        src="https://static.readdy.ai/image/523aa923c70fc5f766f2f9fe4af6dd5e/db007b1f938f14d1fd0033e783b87fae.jpeg"
                        alt="WhatsApp QR Code - Scan to contact Hiroyuki Nagase"
                        className="w-48 h-48 object-contain"
                      />
                      <p className="text-sm text-gray-500 text-center mt-2">Hiroyuki Nagase</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2-Step Form */}
            <div className="bg-gray-50 rounded-3xl p-8">
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-semibold ${formStep === 1 ? 'text-teal-600' : 'text-gray-400'}`}>
                    Step 1: Basic Information
                  </span>
                  <span className={`text-sm font-semibold ${formStep === 2 ? 'text-teal-600' : 'text-gray-400'}`}>
                    Step 2: Optional Details
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: formStep === 1 ? '50%' : '100%' }}
                  ></div>
                </div>
              </div>

              {/* Step 1 Form */}
              {formStep === 1 && (
                <form id="contact-form-step1" className="space-y-6" onSubmit={handleStep1Continue}>
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-900 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-semibold text-gray-900 mb-2">
                      Country of Residence
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="United States"
                    />
                  </div>
                  <div>
                    <label htmlFor="interestedIn" className="block text-sm font-semibold text-gray-900 mb-2">
                      Interested In
                    </label>
                    <select
                      id="interestedIn"
                      name="interestedIn"
                      required
                      value={formData.interestedIn}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="General Information">General Information</option>
                      <option value="Cost Guidance">Cost Guidance</option>
                      <option value="Process Overview">Process Overview</option>
                      <option value="Eligibility Assessment">Eligibility Assessment</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-teal-600 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <span>Continue</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    Informational support only. No medical advice provided.
                  </p>
                </form>
              )}

              {/* Step 2 Form */}
              {formStep === 2 && (
                <form id="contact-form-step2" data-readdy-form className="space-y-6" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="consultedNephrologist" className="block text-sm font-semibold text-gray-900 mb-2">
                      Have you consulted a nephrologist?
                    </label>
                    <select
                      id="consultedNephrologist"
                      name="consultedNephrologist"
                      required
                      value={formData.consultedNephrologist}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="onDialysis" className="block text-sm font-semibold text-gray-900 mb-2">
                      Are you currently on dialysis?
                    </label>
                    <select
                      id="onDialysis"
                      name="onDialysis"
                      required
                      value={formData.onDialysis}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="exploringInternational" className="block text-sm font-semibold text-gray-900 mb-2">
                      Are you exploring international options?
                    </label>
                    <select
                      id="exploringInternational"
                      name="exploringInternational"
                      required
                      value={formData.exploringInternational}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-900 mb-2">
                      Additional Information (Optional)
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      rows={4}
                      maxLength={500}
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                      placeholder="Any additional details you'd like to share..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                  </div>
                  
                  {/* Consent Checkbox */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                        className="mt-1 mr-3 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        I understand this service provides coordination and informational support only. Medical decisions remain with licensed physicians.
                      </span>
                    </label>
                  </div>

                  {formStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                      {!consentChecked ? 'Please accept the consent statement to continue.' : 'Sorry, there was an error. Please try again.'}
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setFormStep(1)}
                      className="flex-1 bg-gray-200 text-gray-700 px-6 py-4 rounded-xl text-base font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      <span>Back</span>
                    </button>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="flex-1 bg-teal-600 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <span>{formStatus === 'submitting' ? 'Sending...' : 'Submit Information Request'}</span>
                      <i className="ri-send-plane-fill"></i>
                    </button>
                  </div>
                </form>
              )}

              {/* Reassurance Text */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-600 text-center leading-relaxed">
                  Your information will be handled confidentially.<br />
                  We respond with general guidance only.<br />
                  This form does not create a medical relationship.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h3 className="text-4xl font-bold mb-6">Japan Ambis International</h3>
              <p className="text-gray-400 text-base leading-relaxed mb-6 max-w-md">
                Dedicated to improving the lives of kidney patients through comprehensive support
                coordination, education, and connecting patients with trusted international medical
                programs.
              </p>
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="w-12 h-12 border-2 border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 border-2 border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                >
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 border-2 border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-12 h-12 border-2 border-gray-700 rounded-full flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-500">QUICK LINKS</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/ethics" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Ethics
                  </Link>
                </li>
                <li>
                  <Link to="/process" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Process
                  </Link>
                </li>
                <li>
                  <Link to="/company" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Company Overview
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold tracking-wider mb-4 text-gray-500">SUPPORT</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Volunteer
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Events
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                    Newsletter
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              © 2025 Japan Ambis International. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer">
                Privacy Policy
              </Link>
              <a href="#" className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer">
                Terms of Service
              </a>
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
